import React, { useEffect, useMemo, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, TextField, Typography,
} from '@mui/material';
import { orderRefFromRow } from '../utils/orderLabel';

/**
 * Модалка часткової відправки на дробарку (брак).
 * Кількість у шт, за замовчуванням — увесь залишок; можна менше.
 */
export default function SendToCrusherDialog({
  open,
  onClose,
  defect,
  onSubmit,
  title = 'Відправити на дробарку',
}) {
  const availPcs = Number(defect?.quantity) || 0;
  const availKg = Number(defect?.weight_kg) || 0;
  const [qty, setQty] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open && defect) {
      setQty(String(availPcs > 0 ? availPcs : ''));
      setError('');
      setLoading(false);
    }
  }, [open, defect, availPcs]);

  const kgHint = useMemo(() => {
    const q = parseInt(qty, 10);
    if (!Number.isFinite(q) || q <= 0 || availPcs <= 0 || availKg <= 0) return null;
    const w = Math.round((availKg * (q / availPcs)) * 1000) / 1000;
    return `≈ ${w} кг з ${availKg} кг`;
  }, [qty, availPcs, availKg]);

  const handleSubmit = async () => {
    const q = parseInt(qty, 10);
    if (!Number.isFinite(q) || q <= 0) {
      setError('Вкажіть коректну кількість');
      return;
    }
    if (q > availPcs) {
      setError(`Доступно лише ${availPcs} шт`);
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onSubmit({ quantity: q, defect });
      onClose?.();
    } catch (e) {
      setError(e?.response?.data?.error || e?.response?.data?.detail || e.message || 'Помилка');
    } finally {
      setLoading(false);
    }
  };

  const label = defect
    ? `${defect.product_name || 'Брак'} · ${defect.order_display || orderRefFromRow(defect)}`
    : '';

  return (
    <Dialog open={open} onClose={() => !loading && onClose?.()} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
        {error && <Typography color="error">{error}</Typography>}
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
        <Typography variant="body2">
          Доступно: <strong>{availPcs} шт</strong>
          {availKg > 0 ? <> · <strong>{availKg} кг</strong></> : null}
        </Typography>
        <TextField
          label="Кількість, шт"
          type="number"
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          fullWidth
          autoFocus
          helperText={kgHint || 'За замовчуванням — увесь залишок; можна ввести менше'}
          inputProps={{ min: 1, max: availPcs, step: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Скасувати</Button>
        <Button
          variant="contained"
          color="error"
          disabled={loading || !qty}
          onClick={handleSubmit}
        >
          {loading ? <CircularProgress size={20} /> : 'На дробарку'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
